import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Package, FloppyDisk } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TVA_OPTIONS = [0, 5, 5.5, 10, 19, 20, 21];
const UNIT_OPTIONS = ['u', 'm', 'm²', 'm³', 'ml', 'km', 'kg', 'L', 'sac', 'palette', 'rouleau', 'lot'];

/**
 * Lightweight modal to create a material in the catalog.
 * Used from the LibraryPage so users can add a raw material that becomes
 * available in the ArticleEditor's material picker.
 *
 * Props:
 *  - show, onClose, onSaved(material), categories=[]
 */
export const MaterialFormModal = ({ show, onClose, onSaved, categories = [] }) => {
  const [form, setForm] = useState({ name: '', unit: 'u', category: '', supplier: '', price_ht: 0, tva_rate: 20, min_threshold: 0 });
  const [saving, setSaving] = useState(false);
  const [supplierList, setSupplierList] = useState([]);

  useEffect(() => {
    if (!show) return;
    setForm({ name: '', unit: 'u', category: '', supplier: '', price_ht: 0, tva_rate: 20, min_threshold: 0 });
    // Fetch existing supplier list from materials catalog so the user can
    // re-use a known supplier without re-typing it.
    axios.get(`${API_URL}/api/materials/catalog`, { withCredentials: true })
      .then(r => {
        const seen = new Set();
        (r.data || []).forEach(m => { if (m.supplier) seen.add(m.supplier); });
        setSupplierList(Array.from(seen).sort((a, b) => a.localeCompare(b, 'fr')));
      })
      .catch(() => {});
  }, [show]);

  if (!show) return null;

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) return toast.error('Nom requis');
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/materials/catalog`, {
        name: form.name.trim(),
        unit: form.unit || 'u',
        category: form.category || '',
        supplier: (form.supplier || '').trim(),
        price_ht: Number(form.price_ht) || 0,
        tva_rate: Number(form.tva_rate) || 20,
        min_threshold: Number(form.min_threshold) || 0,
      }, { withCredentials: true });
      toast.success('Materiau cree');
      if (onSaved) onSaved(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur creation');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="material-form-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 12px', overflowY: 'auto' }}>
      <div style={{ background: 'white', borderRadius: 10, width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Package size={20} /> Nouveau materiau
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid="material-form-close"><X size={22} /></button>
        </div>
        <form onSubmit={handleSave} style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label className="form-label">Nom du materiau</label>
            <input data-testid="material-name" className="form-input" required value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex: Sac de ciment 35kg" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            <div>
              <label className="form-label">Unite</label>
              <Select value={form.unit} onValueChange={(v) => setForm(f => ({ ...f, unit: v }))}>
                <SelectTrigger data-testid="material-unit" style={{ fontSize: '0.85rem', height: 38 }}><SelectValue /></SelectTrigger>
                <SelectContent className="z-[9999]">
                  {UNIT_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="form-label">Categorie (optionnel)</label>
              <Select
                key={`mat-cat-${categories.length}`}
                value={form.category || '__none__'}
                onValueChange={(v) => setForm(f => ({ ...f, category: v === '__none__' ? '' : v }))}
              >
                <SelectTrigger data-testid="material-category" style={{ fontSize: '0.85rem', height: 38 }}>
                  <SelectValue placeholder="Aucune" />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  <SelectItem value="__none__">Aucune</SelectItem>
                  {form.category && !categories.some(c => (c.name || c) === form.category) && (
                    <SelectItem value={form.category}>{form.category}</SelectItem>
                  )}
                  {categories.map(c => (
                    <SelectItem key={c.id || c.name || c} value={c.name || c}>{c.name || c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="form-label">Fournisseur (optionnel)</label>
            <input
              type="text"
              data-testid="material-supplier"
              className="form-input"
              value={form.supplier}
              onChange={(e) => setForm(f => ({ ...f, supplier: e.target.value }))}
              placeholder={supplierList.slice(0, 3).join(', ') || 'Ex: Point P, Saint-Gobain, BigMat...'}
              list="material-supplier-list"
              style={{ fontSize: '0.9rem' }}
            />
            <datalist id="material-supplier-list">
              {supplierList.map(s => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            <div>
              <label className="form-label">Prix HT (EUR)</label>
              <input type="number" step="0.01" min="0" data-testid="material-price"
                className="form-input" value={form.price_ht}
                onChange={(e) => setForm(f => ({ ...f, price_ht: parseFloat(e.target.value) || 0 }))}
                style={{ textAlign: 'right' }} />
            </div>
            <div>
              <label className="form-label">TVA</label>
              <Select value={String(form.tva_rate)} onValueChange={(v) => setForm(f => ({ ...f, tva_rate: parseFloat(v) }))}>
                <SelectTrigger style={{ fontSize: '0.85rem', height: 38 }}><SelectValue /></SelectTrigger>
                <SelectContent className="z-[9999]">
                  {TVA_OPTIONS.map(r => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="form-label">Seuil mini stock</label>
              <input type="number" step="1" min="0" className="form-input" value={form.min_threshold}
                onChange={(e) => setForm(f => ({ ...f, min_threshold: parseFloat(e.target.value) || 0 }))}
                style={{ textAlign: 'right' }} />
            </div>
          </div>
          <div style={{ background: '#F8F9FB', padding: 10, borderRadius: 6, fontSize: '0.78rem', color: '#5E5F66' }}>
            Le materiau sera disponible dans le catalogue (Ressources -&gt; Catalogue) et utilisable dans la composition d'un article.
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" data-testid="material-save" disabled={saving}>
              <FloppyDisk size={16} /> Creer le materiau
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MaterialFormModal;
